import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, DollarSign, Briefcase, Award, Map as MapIcon, List, Flame, Crown, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import CleanerMap from '../components/CleanerMap';
import { useLanguage } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchResults = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [cleaners, setCleaners] = useState([]);
  const [surge, setSurge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingsEnabled, setBookingsEnabled] = useState(true);
  const [waitlistEmail, setWaitlistEmail] = useState(user?.email || '');
  const [waitlistStatus, setWaitlistStatus] = useState('idle'); // idle | submitting | success | error
  const [waitlistMessage, setWaitlistMessage] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [filters, setFilters] = useState({
    minRate: 0,
    maxRate: 200,
    minRating: 0
  });

  const zipCode = searchParams.get('zip');
  const serviceDate = searchParams.get('date');
  const serviceTime = searchParams.get('time');

  useEffect(() => {
    // Gate: must be signed in to see cleaner availability
    if (!user) {
      const next = `/search?${searchParams.toString()}`;
      navigate(`/auth?next=${encodeURIComponent(next)}`);
      return;
    }
    const searchCleaners = async () => {
      try {
        const [searchRes, settingsRes] = await Promise.all([
          axios.post(`${API}/cleaners/search`, {
            zip_code: zipCode,
            service_date: serviceDate,
            service_time: serviceTime
          }),
          axios.get(`${API}/platform/settings`).catch(() => null),
        ]);
        setCleaners(searchRes.data.cleaners || []);
        setSurge(searchRes.data.surge || null);
        if (settingsRes && settingsRes.data) {
          setBookingsEnabled(settingsRes.data.bookings_enabled !== false);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    };

    searchCleaners();
  }, [zipCode, serviceDate, serviceTime]);

  const filteredCleaners = cleaners.filter(cleaner => {
    return cleaner.hourly_rate >= filters.minRate &&
           cleaner.hourly_rate <= filters.maxRate &&
           cleaner.rating_avg >= filters.minRating;
  });

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    setWaitlistStatus('submitting');
    setWaitlistMessage('');
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: waitlistEmail,
        zip_code: zipCode,
        source: 'search-prelaunch',
      });
      setWaitlistStatus('success');
      setWaitlistMessage(res.data.message || 'You\u2019re on the list!');
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistMessage(err.response?.data?.detail || 'Something went wrong. Try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={() => {}} />
      
      <div className="px-6 md:px-12 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-text-primary mb-2" data-testid="search-results-title">
                Available Cleaners
              </h1>
              <p className="text-text-secondary">in {zipCode} on {serviceDate} at {serviceTime}</p>
            </div>
            <div className="flex gap-2 bg-surface rounded-full p-1 border border-border">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'
                }`}
                data-testid="list-view-button"
              >
                <List className="w-4 h-4" />
                <span className="text-sm font-medium">List</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                  viewMode === 'map' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'
                }`}
                data-testid="map-view-button"
              >
                <MapIcon className="w-4 h-4" />
                <span className="text-sm font-medium">Map</span>
              </button>
            </div>
          </div>

          {/* Surge Pricing Banner */}
          {surge && surge.multiplier > 1.0 && (
            <div
              className={`mb-8 rounded-2xl p-5 border-2 flex items-center gap-4 ${
                surge.level === 'peak' ? 'bg-red-50 border-red-300' :
                surge.level === 'high' ? 'bg-orange-50 border-orange-300' :
                'bg-yellow-50 border-yellow-300'
              }`}
              data-testid="surge-banner"
            >
              <div className={`p-3 rounded-full ${
                surge.level === 'peak' ? 'bg-red-100' :
                surge.level === 'high' ? 'bg-orange-100' :
                'bg-yellow-100'
              }`}>
                <Flame className={`w-6 h-6 ${
                  surge.level === 'peak' ? 'text-red-600' :
                  surge.level === 'high' ? 'text-orange-600' :
                  'text-yellow-700'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-text-primary">
                  {surge.label} in {zipCode}
                </h3>
                <p className="text-sm text-text-secondary">
                  Prices are +{surge.premium_percent}% right now ({surge.search_count} searches • {surge.active_cleaners} active cleaners). Book early to lock in your slot.
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-text-secondary uppercase tracking-wide">Multiplier</p>
                <p className="font-heading text-2xl font-bold text-text-primary">{surge.multiplier.toFixed(2)}x</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-2xl p-6 border border-border sticky top-24" data-testid="filters-sidebar">
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">{t('search.filters', 'Filters')}</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Hourly Rate</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        value={filters.minRate}
                        onChange={(e) => setFilters({...filters, minRate: Number(e.target.value)})}
                        className="input-field w-20"
                        placeholder="Min"
                      />
                      <span className="text-text-secondary">to</span>
                      <input
                        type="number"
                        value={filters.maxRate}
                        onChange={(e) => setFilters({...filters, maxRate: Number(e.target.value)})}
                        className="input-field w-20"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Minimum Rating</label>
                    <select
                      value={filters.minRating}
                      onChange={(e) => setFilters({...filters, minRating: Number(e.target.value)})}
                      className="input-field w-full"
                    >
                      <option value="0">Any Rating</option>
                      <option value="3">3+ Stars</option>
                      <option value="4">4+ Stars</option>
                      <option value="4.5">4.5+ Stars</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="text-center py-12">
                  <p className="text-text-secondary">Searching for available cleaners...</p>
                </div>
              ) : filteredCleaners.length === 0 ? (
                !bookingsEnabled ? (
                  <div className="text-center py-12 px-6 bg-gradient-to-br from-primary-light/40 to-surface rounded-3xl border border-primary/20" data-testid="search-prelaunch-message">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                      <span className="text-3xl">🚀</span>
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-text-primary mb-2">We&apos;re launching soon!</h3>
                    <p className="text-text-secondary max-w-md mx-auto mb-6">
                      Match My Cleaner isn&apos;t live for bookings yet — we&apos;re hand-picking and background-checking
                      cleaners in your area right now. Drop your email and you&apos;ll be the first to know when we open
                      in <strong>{zipCode}</strong> — plus a launch-day promo code on us.
                    </p>

                    {waitlistStatus === 'success' ? (
                      <div className="max-w-md mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-5" data-testid="waitlist-success">
                        <p className="text-2xl mb-1">🎉</p>
                        <p className="font-semibold text-emerald-900 mb-1">{waitlistMessage}</p>
                        <p className="text-sm text-emerald-800">Check your inbox for a quick confirmation.</p>
                        <button onClick={() => navigate('/')} className="btn-secondary mt-4" data-testid="prelaunch-back-home">
                          Back to home
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={joinWaitlist} className="max-w-md mx-auto" data-testid="waitlist-form">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="email"
                            required
                            placeholder="you@email.com"
                            value={waitlistEmail}
                            onChange={(e) => setWaitlistEmail(e.target.value)}
                            className="input-field flex-1"
                            data-testid="waitlist-email-input"
                          />
                          <button
                            type="submit"
                            disabled={waitlistStatus === 'submitting'}
                            className="btn-primary whitespace-nowrap disabled:opacity-60"
                            data-testid="waitlist-submit"
                          >
                            {waitlistStatus === 'submitting' ? 'Adding…' : 'Notify me at launch'}
                          </button>
                        </div>
                        {waitlistStatus === 'error' && (
                          <p className="text-sm text-rose-600 mt-2" data-testid="waitlist-error">{waitlistMessage}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate('/')}
                          className="text-sm text-text-secondary hover:text-primary mt-4 underline-offset-2 hover:underline"
                          data-testid="prelaunch-back-home"
                        >
                          ← Back to home
                        </button>
                      </form>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-text-secondary text-lg">{t('search.noResults', 'No cleaners found matching your criteria.')}</p>
                    <button onClick={() => navigate('/')} className="btn-primary mt-6">
                      Search Again
                    </button>
                  </div>
                )
              ) : viewMode === 'map' ? (
                <CleanerMap
                  cleaners={filteredCleaners}
                  onSelectCleaner={(cleaner) => navigate(`/cleaner/${cleaner.id}?${searchParams.toString()}`)}
                  height="700px"
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredCleaners.map((cleaner) => (
                    <div
                      key={cleaner.id}
                      className="bg-surface rounded-2xl p-6 border border-border card-hover cursor-pointer"
                      onClick={() => navigate(`/cleaner/${cleaner.id}?${searchParams.toString()}`)}
                      data-testid="cleaner-profile-card"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-heading text-xl font-semibold text-text-primary">{cleaner.name}</h3>
                            {cleaner.distance_miles != null && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wider"
                                title="Distance from your zip"
                                data-testid={`distance-badge-${cleaner.id}`}
                              >
                                <MapPin className="w-3 h-3" />
                                {cleaner.distance_miles} mi
                              </span>
                            )}
                            {cleaner.tier && cleaner.tier.name !== 'Standard' && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-white text-[10px] font-bold rounded-full uppercase tracking-wider"
                                style={{ backgroundColor: cleaner.tier.color }}
                                data-testid="tier-badge"
                              >
                                <Crown className="w-3 h-3" />
                                {cleaner.tier.name}
                              </span>
                            )}
                            {cleaner.bg_check_status === 'clear' && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full"
                                data-testid="bg-check-badge-card"
                                title="Background-checked"
                              >
                                <ShieldCheck className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-text-secondary">
                            <MapPin className="w-4 h-4" />
                            <span>{cleaner.zip_code}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-primary-light px-3 py-1 rounded-full">
                          <Star className="w-4 h-4 text-primary fill-primary" />
                          <span className="font-semibold text-primary">{cleaner.rating_avg > 0 ? cleaner.rating_avg.toFixed(1) : 'New'}</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <Briefcase className="w-4 h-4" />
                          <span>{cleaner.years_experience} {t('search.yearsExp', 'years experience')}</span>
                        </div>
                        {cleaner.certifications && cleaner.certifications.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-text-secondary">
                            <Award className="w-4 h-4" />
                            <span>{cleaner.certifications.length} certifications</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {cleaner.services && cleaner.services.slice(0, 3).map((service, idx) => (
                          <span key={idx} className="text-xs px-3 py-1 bg-surface-muted text-text-secondary rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-5 h-5 text-primary" />
                          <span className="font-heading text-2xl font-bold text-primary">
                            {cleaner.tier && cleaner.tier.multiplier > 1
                              ? Math.round(cleaner.hourly_rate * cleaner.tier.multiplier)
                              : cleaner.hourly_rate}
                          </span>
                          <span className="text-sm text-text-secondary">{t('search.perHour', '/hr')}</span>
                          {cleaner.tier && cleaner.tier.multiplier > 1 && (
                            <span className="text-xs text-text-secondary line-through ml-1">${cleaner.hourly_rate}</span>
                          )}
                        </div>
                        <button className="btn-primary text-sm" data-testid="view-profile-button">
                          {t('search.viewProfile', 'View Profile')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
